import { CircularProgress, Paper } from "@mui/material";
import { Box } from "@mui/system";
import { cloneDeep, merge, pick } from "lodash";
import React, { Component, useEffect, useMemo } from "react";
import TagManager from "react-gtm-module";
import MapContext from "../../Contexts/MapContext";
import UserContext from "../../Contexts/UserContext";
import {
  axios,
  getConfig,
  getProperties,
  getUserLocation,
} from "../../Services/Data.service";
import { getUser, loginDathic } from "../../Services/auth.service";
import {
  getAnalyticsMode,
  normalizeConsentValue,
  setForceBlockTrackingOnDeny,
} from "../../Utils/consentGate";
import { snowplowCommand, trackSnowplowError } from "../../Utils/snowplow";
import { useRetryRequest } from "../../Utils/hooks";
import { getIsPdp } from "../../Utils/pdpUtils";
import Map from "./Map";
import PoweredByDathic from "./PoweredByDathic";

const sensitiveConfigAttributes = [
  "status",
  "additionalSources",
  "notificationEmail",
];

function parseConsentFromSearch(search) {
  const params = new URLSearchParams(search);
  return {
    analytics: normalizeConsentValue(params.get("consentAnalytics")),
    preferences: normalizeConsentValue(params.get("consentPreferences")),
  };
}

const LoginHandler = ({ loginRequest }) => {
  const retryOnErrors = useMemo(() => [401, 422], []);
  const {
    isRequesting: isLoggingIn,
    requestSuccess: loginSuccess,
    retry: retryLogin,
  } = useRetryRequest(loginRequest, {
    maxTries: 5,
    retryOnErrors,
  });

  useEffect(() => {
    axios.interceptors.response.use(undefined, function (error) {
      let filename = "";
      let branch = "";
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        branch = "response";
        const config = error.response?.config;
        if (retryOnErrors.includes(error.response.status)) {
          // Handle bad request error or auth error
          console.debug(
            "[Home] interceptor: retryable auth/bad-request error",
            { status: error.response.status, url: config?.url }
          );
          retryLogin();
          return Promise.reject(error);
        } else if (
          error.response?.status === 400 &&
          config?.url?.includes("locate_ipaddress")
        ) {
          console.debug(
            "[Home] interceptor: ignoring locate_ipaddress 400",
            { url: config?.url }
          );
          return Promise.reject(error);
        }
        filename =
          config?.method || config?.url
            ? `${config?.method} ${config?.url}`
            : JSON.stringify(config || error.response).trim() ||
              "Data.service.js";
      } else if (error.request) {
        // The request was made but no response was received
        branch = "no-response";
        const config = error.config;
        filename =
          "No response received in Data.service.js: " +
          JSON.stringify({
            method: config?.method,
            url: config?.url,
            code: error.code,
            message: error.message,
            online: navigator.onLine,
          }).trim();
      } else {
        // Something happened in setting up the request that triggered an Error
        branch = "setup-error";
        filename =
          "Error while making request in Data.service.js: " +
          JSON.stringify({
            code: error.code,
            message: error.message,
            stack: error.stack,
          }).trim();
      }
      console.error(`[Home] axios interceptor caught error (${branch})`, {
        filename,
        status: error.response?.status,
        code: error.code,
        message: error.message,
        url: error.config?.url,
        method: error.config?.method,
      });
      trackSnowplowError({
        ...error,
        message: `${error.response?.status || ""} ${
          error.message || "No message provided."
        }`,
        filename,
      });
      return Promise.reject(error);
    });
  }, [retryLogin, retryOnErrors]);

  return !loginSuccess ? (
    <Paper
      sx={{
        position: "absolute",
        zIndex: 3,
        padding: "0px 8px",
        bottom: 0,
        right: 0,
        backgroundColor: "rgba(252,252,252,0.25)",
        color: "#032536",
        fontSize: 11,
        display: "flex",
        alignItems: "center",
      }}
    >
      Preparing the locator...
    </Paper>
  ) : (
    <></>
  );
};

class Home extends Component {
  constructor(props) {
    super(props);
    this.loginRequest = this.loginRequest.bind(this);
    this.consentGatedActions = [];
    this.gtmInitialized = false;
  }

  getCurrentConsent() {
    return (
      this.context.state?.consent || parseConsentFromSearch(window.location.search)
    );
  }

  runWhenConsentAllows(test, action) {
    this.consentGatedActions.push({ test, action, done: false });
    this.evaluateConsentGatedActions();
  }

  evaluateConsentGatedActions() {
    const consent = this.getCurrentConsent();
    this.consentGatedActions.forEach((entry) => {
      if (entry.done) return;
      if (entry.test(consent)) {
        entry.done = true;
        entry.action();
      }
    });
  }

  handleConsentMessage = (event) => {
    if (event.data?.action !== "consent-update") return;
    const consent = {
      analytics: normalizeConsentValue(event.data.analytics),
      preferences: normalizeConsentValue(event.data.preferences),
    };
    this.context.setState((oldState) => ({
      ...(oldState || {}),
      consent,
    }));
    this.syncGtmAnalyticsMode();
  };

  // GTM is loaded once consent resolves, so later consent changes reach it as
  // a dataLayer update that its tag blocking and tracker choice key off.
  syncGtmAnalyticsMode() {
    if (!this.gtmInitialized) return;
    TagManager.dataLayer({
      dataLayer: {
        event: "dathicConsentUpdate",
        dathicAnalyticsMode: getAnalyticsMode(),
      },
    });
  }

  replaceFavicon(url) {
    var link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.getElementsByTagName("head")[0].appendChild(link);
    }
    link.href = url;
  }

  async checkAdminAuth() {
    const params = new URLSearchParams(window.location.search);
    const adminToken = params.get("adminToken");
    const adminUserId = params.get("adminUserId");
    if (!adminToken || !adminUserId) {
      return;
    }

    const userFromDB = await getUser(adminUserId, adminToken);
    if (userFromDB && !userFromDB.length) {
      if (userFromDB.active === false) {
        return;
      }
      const resources = userFromDB.roles?.reduce(
        (resources, roleItem) =>
          Array.from(
            new Set([
              ...resources,
              ...(roleItem.role.resources?.map(
                (resource) => resource.resource_id
              ) || []),
            ])
          ),
        []
      );
      let adminUser = {
        ...userFromDB,
        resources,
      };
      this.context.setState((oldState) => ({
        ...(oldState || {}),
        adminUser,
      }));
    } else {
      return;
    }
  }

  async loginRequest() {
    const params = new URLSearchParams(window.location.search);
    try {
      let dathicCredential = await loginDathic(
        process.env.REACT_APP_DEFAULT_USERNAME,
        process.env.REACT_APP_DEFAULT_USERPWD
      );
      if (!dathicCredential?.access_token?.length) {
        return;
      }
      const { user_id, access_token: accessToken } = dathicCredential;
      sessionStorage.setItem("dathic_access_token", accessToken);
      let properties = await getProperties();
      let config = await getConfig();
      const storeLocatorConfigFromParam = JSON.parse(
        decodeURIComponent(params.get("storeLocatorConfig") || "{}")
      );
      const storeLocatorConfig = cloneDeep(config?.store_locator || {});
      if (storeLocatorConfig?.initialCoordinatesFromIP ?? true) {
        const countries = properties?.find((p) => p.name === "countries")
          ?.value;
        const fetchIpLocation = async () => {
          try {
            const locationRes = await getUserLocation(countries);
            if (locationRes && typeof locationRes !== "string") {
              return {
                latitude: locationRes.location.latitude,
                longitude: locationRes.location.longitude,
              };
            }
          } catch (e) {
            console.log(e);
          }
          return null;
        };
        const geolocationAllowed = (consent) =>
          consent.preferences === "yes" && consent.analytics === "yes";
        if (geolocationAllowed(this.getCurrentConsent())) {
          const coords = await fetchIpLocation();
          if (coords) {
            merge(storeLocatorConfig, { initialCoordinates: coords });
          }
        } else {
          this.runWhenConsentAllows(geolocationAllowed, async () => {
            const coords = await fetchIpLocation();
            if (coords) {
              this.context.setState((oldState) => ({
                ...(oldState || {}),
                config: {
                  ...(oldState?.config || {}),
                  initialCoordinates: coords,
                },
              }));
            }
          });
        }
      }
      merge(
        storeLocatorConfig,
        pick(
          storeLocatorConfigFromParam,
          Object.keys(storeLocatorConfigFromParam).filter(
            (key) => !sensitiveConfigAttributes.includes(key)
          )
        )
      );
      if (storeLocatorConfigFromParam?.searchFilters) {
        storeLocatorConfig.searchFilters = storeLocatorConfigFromParam.searchFilters;
      }
      this.context.setState((oldState) => ({
        ...(oldState || {}),
        accessToken: accessToken,
        userId: user_id,
        properties,
        config: storeLocatorConfig,
        orgProperties: config?.properties,
        productDetailPage: config?.product_detail_page,
      }));
      const organizationId = window.DATHIC_ORG_ID;
      const org_id = organizationId || process.env.REACT_APP_ORG_ID;
      const initializeGtm = () => {
        TagManager.initialize({
          gtmId: "GTM-M7P8TV3",
          events: {
            flavorFilterChange: "flavorFilterChange",
            chainFilterChange: "chainFilterChange",
            typeFilterChange: "typeFilterChange",
            componentVisible: "componentVisible",
            searchChange: "searchChange",
          },
          dataLayer: {
            GAMeasurementId: storeLocatorConfig?.GAMeasurementId,
            UATrackingId: storeLocatorConfig?.UATrackingId,
            organization_id: org_id,
            dathicAnalyticsMode: getAnalyticsMode(),
          },
        });
        this.gtmInitialized = true;
      };
      // Must be reported before the GTM decision below: the analytics mode
      // for denied consent depends on it.
      setForceBlockTrackingOnDeny(storeLocatorConfig?.forceBlockTrackingOnDeny ?? false);
      const analyticsAllowed = () => getAnalyticsMode() !== "blocked";
      if (analyticsAllowed()) {
        initializeGtm();
      } else {
        this.runWhenConsentAllows(analyticsAllowed, initializeGtm);
      }
      if (storeLocatorConfig?.faviconUrl) {
        this.replaceFavicon(storeLocatorConfig.faviconUrl);
      }
      return accessToken;
    } catch (e) {
      console.log(e);
      return;
    }
  }

  componentDidMount() {
    snowplowCommand("enableFormTracking");
    this.checkAdminAuth();
    this.context.setState((oldState) => ({
      ...(oldState || {}),
      consent: parseConsentFromSearch(window.location.search),
    }));
    window.addEventListener("message", this.handleConsentMessage);
  }

  componentWillUnmount() {
    window.removeEventListener("message", this.handleConsentMessage);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.context.state?.config) {
      snowplowCommand("enableFormTracking");
    }
    this.evaluateConsentGatedActions();
  }

  render() {
    const { region } = this.props.match?.params || {};
    const { path = "" } = this.props.match || {};
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const storelocatorInactive =
      this.context.state?.config?.status === "inactive";

    const containerHeight = window !== window.top ? "100%" : window.innerHeight;
    return storelocatorInactive ? (
      <></>
    ) : (
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        width="100vw"
        height={containerHeight}
      >
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          height={isMobile ? undefined : "100%"}
        >
          <Box flex={1} width="100%">
            {!getIsPdp() && (
              <PoweredByDathic
                disableAttributionLink={
                  this.context.state?.config?.disableAttributionLink ?? false
                }
              />
            )}
            {this.context.state?.config ? (
              <MapContext.Consumer>
                {(mapContext) => (
                  <Map
                    region={region}
                    storeLocatorConfig={this.context.state?.config}
                    orgProperties={this.context.state?.orgProperties}
                    mapContext={mapContext}
                    isPdp={getIsPdp()}
                  />
                )}
              </MapContext.Consumer>
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </div>
            )}
            <LoginHandler loginRequest={this.loginRequest} />
          </Box>
        </Box>
      </Box>
    );
  }
}
Home.contextType = UserContext;

export default Home;
